import { useState } from 'react';
import { supabase } from '../services/supabase';
import { otpSend, otpVerify, ApiError } from '../services/api';
import { LOGIN } from '../tour/copy';
import logoReversed from '../assets/logos/Cornerstone-reversed.svg';
import logoColor from '../assets/logos/Cornerstone-color.svg';

// One-pager for Cornerstone Bulk Materials' Yard-Sourcing Tool. Left column is
// the explanatory sign-in (what it does → sign-in box → benefits); the brand
// story sits on the right. Auth logic is unchanged.
export default function Login() {
  return (
    <div className="onepager">
      {/* LEFT — explanatory sign-in: what it does → sign in → benefits */}
      <section className="op-login">
        <div className="op-login-inner">
          <img
            src={logoColor}
            alt="Cornerstone Bulk Materials"
            className="op-login-logo"
          />
          <div className="op-login-eyebrow">
            Cornerstone Bulk Materials · a Perform Wiser tool
          </div>
          <h1 className="op-login-title">Yard-Sourcing Tool</h1>

          <div className="op-list-h">What it does</div>
          <ul className="op-list">
            <li>Prices every delivery live from your own delivery history</li>
            <li>Ranks each yard by true delivered price — material plus freight</li>
            <li>Checks which yards actually have the material in stock</li>
            <li>Drafts the order and delivery ticket in one click</li>
          </ul>

          <AuthCard />

          <div className="op-list-h">Why teams use it</div>
          <ul className="op-list op-list--benefit">
            <li>Always source the cheapest yard — no manual map work</li>
            <li>Reduce cost lost to freight — up to $30 per load</li>
            <li>Save 6+ hours a week on sourcing</li>
            <li>Protect inventory across every yard</li>
          </ul>
        </div>
      </section>

      {/* RIGHT — the brand story */}
      <section className="op-story">
        <img src={logoReversed} alt="Cornerstone Bulk Materials" className="op-logo" />
        <div className="op-eyebrow">From wall map to one click</div>
        <h1 className="op-h1">
          Which yard delivers it <span className="hl">cheapest?</span>
        </h1>
        <p className="op-lead">
          Freight can be <strong>half the cost</strong> of a bulk-materials load — a $35/yd load of
          mulch can carry $200+ of delivery. The Yard-Sourcing Tool prices every delivery live from
          your own history and auto-picks the cheapest yard that can actually fill it.
        </p>

        <div className="op-arc">
          <ArcStep
            k="Before"
            t="Sourced by hand"
            d="Owner Ray reviewed every job himself on a wall map — about 90 minutes every morning."
            icon={<ClockIcon />}
          />
          <span className="op-arrow">→</span>
          <ArcStep
            k="Then"
            t="Outgrew him"
            d="1 → 4 yards, 300+ deliveries a month. A dozen lanes per job — too many to hold in your head."
            icon={<GrowthIcon />}
          />
          <span className="op-arrow">→</span>
          <ArcStep
            now
            k="Now"
            t="Automatic"
            d="Ranks every yard by delivered price, checks stock, and drafts the order — in one click."
            icon={<BoltIcon />}
          />
        </div>

        <div className="op-since">Since rolling it out</div>
        <div className="op-stats">
          <Stat n="6+" u="hrs/wk saved" />
          <Stat n="100%" u="sourced cheapest" />
          <Stat n="$55" u="/load recovered" />
          <Stat n="300+" u="orders/mo tracked" />
        </div>

        <div className="op-pills">
          {['Cheapest yard', 'Protect inventory', 'One-click orders', 'Live map', 'AI chat'].map(
            (p) => (
              <span key={p} className="op-pill">
                {p}
              </span>
            )
          )}
        </div>

        <p className="op-quote">
          “It does what I used to do in my head — except it never has an off day. Now I can actually
          go run the company.”
          <span className="op-quote-by">— Ray Holloway, Owner</span>
        </p>
      </section>
    </div>
  );
}

function ArcStep({
  k,
  t,
  d,
  icon,
  now,
}: {
  k: string;
  t: string;
  d: string;
  icon: React.ReactNode;
  now?: boolean;
}) {
  return (
    <div className={`op-step ${now ? 'now' : ''}`}>
      <div className="op-step-top">
        {icon}
        <span className="op-step-k">{k}</span>
      </div>
      <div className="op-step-t">{t}</div>
      <div className="op-step-d">{d}</div>
    </div>
  );
}

function Stat({ n, u }: { n: string; u: string }) {
  return (
    <div className="op-stat">
      <div className="op-stat-n">{n}</div>
      <div className="op-stat-u">{u}</div>
    </div>
  );
}

// Email one-time passcode sign-in. Logic unchanged.
function AuthCard() {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setInfo(null);
    setBusy(true);
    try {
      await otpSend(email.trim());
      setStep('code');
      setInfo(`We emailed a sign-in code to ${email.trim()}.`);
    } catch (e: any) {
      const detail = e instanceof ApiError ? e.message : e?.message ?? 'Could not send code';
      setErr(detail);
      setStep('code');
      setInfo('If the email did not arrive, ask the operator for the code (server console).');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { session } = await otpVerify(email.trim(), code.trim());
      const { error } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });
      if (error) throw error;
      // onAuthStateChange in useAuth picks up the session and routes to the app.
    } catch (e: any) {
      setErr(e?.message ?? 'Invalid or expired code');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card op-card">
      {step === 'email' ? (
        <form onSubmit={sendCode}>
          <h3 className="op-card-title">Sign in to explore</h3>
          <p className="muted op-hint" style={{ marginTop: 0, marginBottom: 12 }}>
            No account needed — it’s a live demo.
          </p>
          <label className="field">Email</label>
          <input
            className="input"
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <p className="muted op-hint">{LOGIN.emailHint}</p>
          {err && <p className="op-err">{err}</p>}
          <button className="btn btn-primary op-submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </form>
      ) : (
        <form onSubmit={verify}>
          <h3 className="op-card-title">Enter code</h3>
          {info && (
            <p className="muted op-hint" style={{ marginTop: 0, marginBottom: 12 }}>
              {info}
            </p>
          )}
          <label className="field">Sign-in code</label>
          <input
            className="input"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="12345678"
            required
          />
          <p className="muted op-hint">{LOGIN.codeHint}</p>
          {err && <p className="op-err">{err}</p>}
          <button className="btn btn-primary op-submit" disabled={busy}>
            {busy ? 'Verifying…' : 'Verify & sign in'}
          </button>
          <button
            type="button"
            className="btn op-alt"
            onClick={() => {
              setStep('email');
              setCode('');
              setErr(null);
              setInfo(null);
            }}
          >
            Use a different email
          </button>
        </form>
      )}
    </div>
  );
}

/* ---------- inline icons ---------- */
function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ico" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" />
    </svg>
  );
}
function GrowthIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ico" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 19V5M4 19h16" strokeLinecap="round" />
      <path d="M8 16l4-5 3 3 4-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function BoltIcon() {
  return (
    <svg viewBox="0 0 24 24" className="ico" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" strokeLinejoin="round" />
    </svg>
  );
}
